// Thin wrapper around the Screen Wake Lock API. Keeps the device screen on
// during an active run so the browser doesn't suspend `geolocation.watchPosition`
// (browsers kill GPS tracking when the device locks the screen — there's no
// way around this for a web app without going native).
//
// Re-acquires the lock automatically when the tab becomes visible again
// (mobile browsers release it on page-hide).
//
// Usage:
//   const wake = useWakeLock(isRunActive);
//   wake.supported, wake.active, wake.error
//
import { useEffect, useRef, useState } from 'react';

export function useWakeLock(enabled) {
  const [supported] = useState(() => typeof navigator !== 'undefined' && 'wakeLock' in navigator);
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!supported || !enabled) {
      // Release any held sentinel when the flag flips off.
      if (sentinelRef.current) {
        sentinelRef.current.release().catch(() => {});
        sentinelRef.current = null;
        setActive(false);
      }
      return;
    }

    let cancelled = false;

    async function acquire() {
      try {
        const s = await navigator.wakeLock.request('screen');
        if (cancelled) {
          s.release().catch(() => {});
          return;
        }
        sentinelRef.current = s;
        setActive(true);
        s.addEventListener('release', () => {
          setActive(false);
        });
      } catch (err) {
        setError(err?.message || 'wake lock denied');
      }
    }

    acquire();

    const onVis = () => {
      if (document.visibilityState === 'visible' && enabled && !sentinelRef.current) {
        acquire();
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      if (sentinelRef.current) {
        sentinelRef.current.release().catch(() => {});
        sentinelRef.current = null;
      }
    };
  }, [supported, enabled]);

  return { supported, active, error };
}
